import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { useAppStore } from '../store.js';
import {
  acknowledgeResult,
  archiveResult,
  boundedResultWait,
  canRetryResult,
  pendingResults,
  resultHistory,
  queueResult,
  detachResultJournal,
  hasDurableResult,
  exportResultJournal,
  type PendingResult,
} from '../result-outbox.js';

export function PendingResults() {
  const owner = useAppStore((state) => state.session?.user.internalId);
  return owner ? <OwnedPendingResults key={owner} owner={owner} /> : null;
}

function OwnedPendingResults({ owner }: { owner: string }) {
  const { pathname } = useLocation();
  const client = useQueryClient();
  const [entries, setEntries] = useState(() => resultHistory(owner));
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<{ text: string; destination: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [storageError, setStorageError] = useState('');
  const retry = useRef<() => void>(() => {});
  const busy = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const playing = pathname.startsWith('/game/') || pathname.startsWith('/path/game/');
  useEffect(() => {
    const open = () => {
      setEntries(resultHistory(owner));
      setHistoryOpen(true);
    };
    window.addEventListener('blockbattle:result-history', open);
    return () => window.removeEventListener('blockbattle:result-history', open);
  }, [owner]);
  useEffect(() => {
    if (!historyOpen) return;
    const previous = document.activeElement;
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [historyOpen]);
  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => setSuccess(null), 10_000);
    return () => clearTimeout(timer);
  }, [success]);
  useEffect(() => {
    if (playing) return;
    let active = true;
    const currentOwner = () => useAppStore.getState().session?.user.internalId === owner;
    const refresh = () => {
      if (currentOwner()) setEntries(resultHistory(owner));
    };
    const send = async () => {
      if (busy.current || !active || !currentOwner()) return;
      refresh();
      const pending = pendingResults(owner).filter((entry) => entry.retryable !== false);
      if (!pending.length) return;
      busy.current = true;
      setSending(true);
      try {
        for (const entry of pending) {
          if (!active || !currentOwner()) break;
          // A different tab may already have acknowledged this entry.
          if (!pendingResults(owner).some((item) => item.id === entry.id)) continue;
          try {
            let text: string;
            if (entry.kind === 'PATH') {
              const result = await boundedResultWait(
                api.finishPathRun(entry.id, entry.actions ?? []),
              );
              text = `Результат сохранён: ${result.score.toLocaleString('ru-RU')}`;
            } else {
              const result = await boundedResultWait(
                api.finishRun(entry.id, entry.token, entry.moves, entry.actions, entry.forfeit),
              );
              text = result.review
                ? 'Партия отправлена на проверку'
                : `Результат сохранён: ${result.score.toLocaleString('ru-RU')}`;
              if (currentOwner())
                useAppStore.getState().setLastResult({ ...result, runId: entry.id, mode: 'DAILY' });
            }
            if (!currentOwner()) break;
            acknowledgeResult(owner, entry.id);
            for (const key of ['path', 'rewards', 'reward-cards', 'home', 'leagues', 'leaderboard'])
              void client.invalidateQueries({ queryKey: [key] });
            if (currentOwner())
              setSuccess({ text, destination: entry.kind === 'PATH' ? '/path' : '/result' });
          } catch (error) {
            if (!currentOwner()) break;
            // Never resurrect a result acknowledged or archived in another tab.
            if (!pendingResults(owner).some((item) => item.id === entry.id)) continue;
            queueResult(owner, {
              ...entry,
              error: error instanceof Error ? error.message : 'Нет связи с сервером',
              retryable: canRetryResult(error),
            });
            if (!canRetryResult(error) && hasDurableResult(owner, entry.id))
              detachResultJournal(entry);
          }
          refresh();
        }
      } finally {
        busy.current = false;
        setSending(false);
      }
    };
    retry.current = () => void send();
    void send();
    const timer = window.setInterval(() => void send(), 30_000);
    const resume = () => {
      if (!document.hidden) void send();
    };
    window.addEventListener('online', resume);
    window.addEventListener('storage', refresh);
    document.addEventListener('visibilitychange', resume);
    return () => {
      active = false;
      retry.current = () => {};
      clearInterval(timer);
      window.removeEventListener('online', resume);
      window.removeEventListener('storage', refresh);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [owner, playing, client]);
  const archive = (entry: PendingResult) => {
    if (!archiveResult(owner, entry.id))
      setStorageError(
        'Не удалось сохранить журнал в истории. Скачайте его перед очисткой памяти устройства.',
      );
    else setStorageError('');
    setEntries(resultHistory(owner));
  };
  const download = (entry: PendingResult) => {
    const url = URL.createObjectURL(
      new Blob([exportResultJournal(entry)], { type: 'application/json' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `blockbattle-result-${entry.id}.json`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };
  const pending = entries.filter((entry) => !entry.archivedAt);
  const retryable = pending.filter((entry) => entry.retryable !== false);
  const terminal = pending.filter((entry) => entry.retryable === false);
  if (playing) return null;
  return (
    <>
      {pending.length ? (
        <aside className="pending-result-notice" role="status">
          <span>
            {sending
              ? 'Сохраняем результат предыдущей партии…'
              : retryable.length
                ? 'Результат пока не подтверждён. Журнал сохранён. Повторим отправку при подключении.'
                : 'Сервер не принял результат. Журнал сохранён в истории.'}
            {pending.length > 1 ? ` Партий: ${pending.length}.` : ''}
          </span>
          {retryable.length > 0 ? (
            <button disabled={sending} onClick={() => retry.current()}>
              {sending ? 'Отправка…' : 'Повторить'}
            </button>
          ) : null}
          <button onClick={() => setHistoryOpen(true)}>Подробнее</button>
          {!retryable.length && terminal.length ? (
            <button aria-label="Убрать уведомление" onClick={() => terminal.forEach(archive)}>
              ×
            </button>
          ) : null}
        </aside>
      ) : null}
      {success ? (
        <aside className="pending-result-notice" role="status">
          <span>{success.text}</span>
          <Link to={success.destination} onClick={() => setSuccess(null)}>
            Посмотреть
          </Link>
          <button aria-label="Закрыть подтверждение" onClick={() => setSuccess(null)}>
            ×
          </button>
        </aside>
      ) : null}
      {storageError ? <p role="alert">{storageError}</p> : null}
      {historyOpen ? (
        <dialog
          ref={dialog}
          className="result-history-dialog"
          aria-labelledby="result-history-title"
          onCancel={() => setHistoryOpen(false)}
        >
          <header>
            <h2 id="result-history-title">История отправки</h2>
            <button aria-label="Закрыть историю" onClick={() => setHistoryOpen(false)}>
              ×
            </button>
          </header>
          <p>Журналы хранятся на этом устройстве. Они не означают, что очки засчитаны.</p>
          {!entries.length ? <p>Неподтверждённых партий нет.</p> : null}
          {entries.map((entry) => (
            <article key={entry.id}>
              <strong>
                {entry.kind === 'PATH' ? 'Путь' : 'Партия'} ·{' '}
                {new Date(entry.at).toLocaleString('ru-RU')}
              </strong>
              <p>
                {entry.retryable === false
                  ? 'Сервер отклонил отправку. Повтор не устранит эту причину.'
                  : 'Ожидает подтверждения. Отправка повторяется автоматически.'}
              </p>
              {entry.error ? <p>{entry.error}</p> : null}
              <small>Номер: {entry.id}</small>
              <div>
                <button onClick={() => download(entry)}>Скачать журнал</button>
                {entry.archivedAt ? (
                  <small>В архиве · очки не подтверждены</small>
                ) : entry.retryable === false ? (
                  <button onClick={() => archive(entry)}>Убрать уведомление</button>
                ) : (
                  <button disabled={sending} onClick={() => retry.current()}>
                    {sending ? 'Отправка…' : 'Повторить'}
                  </button>
                )}
              </div>
            </article>
          ))}
        </dialog>
      ) : null}
    </>
  );
}
